import { formatDistanceToNow } from "date-fns";
import { Monitor, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useRealtimeChannel } from "@/hooks/useRealtimeChannel";
import { supabase } from "@/integrations/supabase/client";
import { formatLocationForDisplay } from "@/lib/sessionInfoFormatter";

interface Session {
  session_id: string;
  session_token: string;
  device_name: string;
  ip_address: string;
  city: string;
  state?: string;
  country: string;
  is_current_session: boolean;
  is_trusted_device: boolean;
  last_activity_at: string;
  created_at: string;
  expires_at: string;
}

export const ActiveSessionsManager = () => {
  const { user } = useAuth();
  const { execute: invoke } = useEdgeFunction({ auth: true });
  const queryClient = useQueryClient();
  const queryKey = ['activeSessions', user?.id];

  const { data: sessions = [], isLoading: loading } = useQuery<Session[]>({
    queryKey,
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase.rpc('get_active_sessions' as any, {
        p_user_id: user.id
      });
      if (error) {
        console.error('Failed to load sessions:', error);
        throw error;
      }
      const currentToken = localStorage.getItem('current_session_token');
      return ((data as any) || []).map((s: any) => ({
        ...s,
        is_current_session: s.session_token === currentToken
      }));
    },
    enabled: !!user,
  });

  useRealtimeChannel({
    channelName: user?.id ? `active-sessions:${user.id}` : '',
    type: 'postgres_changes',
    event: '*',
    schema: 'public',
    table: 'auth_sessions',
    filter: user?.id ? `user_id=eq.${user.id}` : undefined,
    onMessage: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const terminateSessionMutation = useMutation({
    mutationFn: (sessionId: string) => invoke('terminate-session', {
      body: { session_id: sessionId, reason: 'user_revoked' },
      showErrorToast: true
    }),
    onSuccess: () => {
      toast.success("Session terminated successfully");
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const terminateAllOthersMutation = useMutation({
    mutationFn: async () => {
      const currentToken = localStorage.getItem('current_session_token');
      if (!currentToken) {
        throw new Error("Current session not found");
      }
      const { data, error } = await supabase.rpc('terminate_all_other_sessions' as any, {
        p_user_id: user?.id,
        p_current_session_token: currentToken,
        p_reason: 'user_revoked_all',
        p_trigger_event: 'manual_terminate_all'
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      toast.success(`${data?.terminated_count || 0} session(s) terminated`);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to terminate sessions");
    },
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Active Sessions</CardTitle>
        <CardDescription>
          Manage devices that are currently signed in to your account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {sessions.length === 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              No active sessions found. This may indicate a synchronization issue.
            </AlertDescription>
          </Alert>
        )}

        {sessions.map((session) => (
          <div 
            key={session.session_id} 
            className="flex items-center justify-between p-4 border rounded-lg"
          >
            <div className="flex items-center gap-4">
              {session.is_current_session ? (
                <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
              ) : (
                <Monitor className="h-5 w-5 text-muted-foreground shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  <span className="truncate">{session.device_name || 'Unknown Device'}</span>
                  {session.is_current_session && (
                    <Badge variant="outline">Current</Badge>
                  )}
                  {session.is_trusted_device && (
                    <Badge variant="secondary">Trusted</Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatLocationForDisplay({
                    city: session.city,
                    state: session.state,
                    country: session.country
                  })}
                </div>
                <div className="text-xs text-muted-foreground">
                  Last active: {formatDistanceToNow(new Date(session.last_activity_at))} ago
                </div>
              </div>
            </div>
            {!session.is_current_session && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => terminateSessionMutation.mutate(session.session_id)}
                disabled={terminateSessionMutation.isPending && terminateSessionMutation.variables === session.session_id}
              >
                {terminateSessionMutation.isPending && terminateSessionMutation.variables === session.session_id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Terminate'
                )}
              </Button>
            )}
          </div>
        ))}

        {sessions.length > 1 && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => terminateAllOthersMutation.mutate()}
            disabled={terminateAllOthersMutation.isPending}
          >
            {terminateAllOthersMutation.isPending ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Terminating...</>
            ) : (
              'Terminate All Other Sessions'
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
